import { Link, useRouterState } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { can, useMe } from '../auth.tsx';
import { PageHeader, Panel } from '../components/ui.tsx';
import { HomeCards } from '../components/home-cards.tsx';
import { ExecutiveDashboard } from './ExecutiveDashboard.tsx';
import { DEPARTMENT_CARDS } from './home-cards.ts';
import { PurchaseHomeIndicators } from './purchasing/HomeIndicators.tsx';
import {
  AREAS,
  groupsOf,
  itemsOf,
  type NavItem,
  screensOf,
  sectionAt,
  visibleAreas,
} from '../navigation.ts';

/** Cartões com as telas de um grupo, para abrir com um clique. */
export function ScreenGrid({ items }: { items: NavItem[] }) {
  const { t } = useTranslation();
  return (
    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <li key={item.to}>
            <Link
              to={item.to}
              className="flex min-h-10 items-center gap-3 rounded-md border border-rule px-3 py-3 text-sm text-ink hover:border-control-border hover:bg-paper"
            >
              <Icon size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
              {t(item.key)}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Página inicial de um departamento: apresenta o departamento e lista os seus módulos e
 * telas, a partir do menu (navigation.ts). Sem telas ainda, avisa que está em preparação.
 */
export function DepartmentHomePage() {
  const { t } = useTranslation();
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const areas = useMemo(() => visibleAreas((p) => can(me.data, p)), [me.data]);

  const isHome = (a: (typeof AREAS)[number]) => itemsOf(a)[0]?.to === pathname;
  const declared = AREAS.find(isHome);
  if (!declared) return null;
  const area = areas.find(isHome);
  const header = (
    <PageHeader title={t(declared.key)} description={t(`departmentHome.about.${declared.id}`)} />
  );

  if (!area) {
    return (
      <>
        {header}
        <Panel>
          <p className="text-sm text-muted">{t('departmentHome.noAccess')}</p>
        </Panel>
      </>
    );
  }

  if (declared.id === 'executive') {
    return (
      <>
        {header}
        <ExecutiveDashboard />
      </>
    );
  }

  if (screensOf(declared).length === 0) {
    return (
      <>
        {header}
        <Panel title={t('departmentHome.preparingTitle')}>
          <p className="text-sm text-muted">{t('departmentHome.preparing')}</p>
        </Panel>
      </>
    );
  }

  const cards = DEPARTMENT_CARDS[declared.id];
  return (
    <>
      {header}
      {declared.id === 'purchasing' && <PurchaseHomeIndicators />}
      {cards && <HomeCards config={cards} />}
      {area.groups.map((group, gi) => {
        const screens = group.items.filter((i) => !i.overview);
        if (screens.length === 0) return null;
        return (
          <Panel key={group.key ?? gi} title={t(group.key ?? 'departmentHome.screens')}>
            <ScreenGrid items={screens} />
          </Panel>
        );
      })}
    </>
  );
}

/**
 * Página de uma seção do menu enxuto (ex.: Dados Mestres › Estrutura corporativa): apresenta a
 * seção e lista as telas dela que a pessoa pode abrir. Seção com mais de um grupo (Plataforma)
 * mostra um bloco por grupo.
 */
export function SectionHomePage() {
  const { t } = useTranslation();
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const areas = useMemo(() => visibleAreas((p) => can(me.data, p)), [me.data]);

  const declared = sectionAt(AREAS, pathname);
  if (!declared) return null;
  const { section } = declared;
  const area = areas.find((a) => a.id === declared.area.id);
  const groups = area ? groupsOf(area, section) : [];

  return (
    <>
      <PageHeader
        title={t(section.key)}
        description={t(`departmentHome.sectionAbout.${section.id}`)}
      />
      {groups.length === 0 ? (
        <Panel>
          <p className="text-sm text-muted">{t('departmentHome.noAccess')}</p>
        </Panel>
      ) : (
        groups.map((group, gi) => (
          <Panel
            key={group.key ?? gi}
            title={groups.length > 1 && group.key ? t(group.key) : undefined}
          >
            <ScreenGrid items={group.items} />
          </Panel>
        ))
      )}
    </>
  );
}
